import { Injectable } from '@angular/core';
import { AttachmentError, AttachmentErrorType } from '../../domain/errors/attachment-error';

/**
 * Maps structured attachment errors to user-friendly messages.
 * Keeps presentation concerns out of the repository layer.
 */
@Injectable({ providedIn: 'root' })
export class AttachmentErrorMapperService {
  /**
   * Convert AttachmentError to user-friendly message.
   * Fallback to original message if error is not AttachmentError.
   */
  public toUserMessage(error: unknown): string {
    if (error instanceof AttachmentError) {
      return this._mapErrorTypeToMessage(error.type, error.originalMessage);
    }

    // Fallback for non-AttachmentError
    if (error instanceof Error) {
      return error.message;
    }

    return 'An unexpected error occurred';
  }

  /**
   * Map error type to user-friendly message.
   * For FILE_TOO_LARGE and FILE_TYPE_NOT_ALLOWED, prefer backend message (HCMI-706).
   */
  private _mapErrorTypeToMessage(type: AttachmentErrorType, originalMessage?: string): string {
    switch (type) {
      case AttachmentErrorType.FILE_TOO_LARGE:
        // Use backend message if available (contains actual limit and file size)
        return originalMessage && originalMessage.includes('MB')
          ? originalMessage
          : 'File size exceeds the maximum allowed limit';

      case AttachmentErrorType.FILE_TYPE_NOT_ALLOWED:
        // Use backend message if available (lists allowed types)
        return originalMessage || 'File type is not allowed';

      case AttachmentErrorType.CASE_CLOSED:
        return 'Cannot perform this action - case is closed or resolved';

      case AttachmentErrorType.UPLOAD_EXPIRED:
        return 'Upload session expired. Please try again';

      case AttachmentErrorType.NETWORK_ERROR:
        return 'Network error. Please check your connection';

      case AttachmentErrorType.S3_UPLOAD_FAILED:
        return 'File upload to storage failed. Please try again';

      case AttachmentErrorType.UPLOAD_ABORTED:
        return 'Upload was cancelled';

      case AttachmentErrorType.PERMISSION_DENIED:
        return 'You do not have permission to perform this action';

      case AttachmentErrorType.NOT_FOUND:
        return 'Attachment not found';

      case AttachmentErrorType.UNKNOWN:
      default:
        return 'An unexpected error occurred. Please try again';
    }
  }
}
